import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {
    Avatar,
    Box,
    Typography,
    Card,
    CardHeader,
    CardContent,
    CardMedia,
    ButtonBase,
} from '@material-ui/core';
import {makeStyles, useTheme} from '@material-ui/core/styles';
import randomColor from 'random-material-color';
import {BrokenImage} from '@material-ui/icons';
import get from 'lodash.get';
import Show from 'components/utils/Show';
import clsx from 'clsx';

const useHeaderStyles = makeStyles({
    root: {},
    avatar: {},
    action: {},
    content: {
        width: '50%',
    },
    title: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    subheader: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
});

const useStyles = makeStyles(theme => ({
    root: {
        [theme.breakpoints.down('sm')]: {
            width: '20em',
        },
        [theme.breakpoints.up('md')]: {
            width: '30em',
        },
    },
    listItemRoot: {
        display: 'flex',
        direction: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '15em',
        [theme.breakpoints.up('sm')]: {
            width: '30em',
        },
    },
    listItem: {
        background:
            theme.palette.type === 'dark'
                ? theme.palette.grey.medium
                : theme.palette.grey.light,
    },
    listItemSelected: {
        background:
            theme.palette.type === 'dark'
                ? theme.palette.primary.light
                : theme.palette.primary.light4,
    },
    subtitle: {
        paddingTop: '1em',
        whiteSpace: 'pre-line',
        lineHeight: '.75',
    },
    cover: {
        paddingTop: '56%',
        height: 0,
    },
    noImage: {
        paddingTop: '56%',
        height: 0,
        textAlign: 'center',
        position: 'relative',
        '& > *': {
            fontSize: '120pt',
            color: theme.palette.grey[300],
            position: 'absolute',
            left: 'calc((100% - 120pt) / 2)',
            top: 'calc((100% - 120pt) / 2)',
        },
    },
}));

const DocumentCard = ({
    document,
    title,
    subtitle,
    start_date,
    end_date,
    variant = 'default',
    onClick,
    selected,
    otherData,
    error,
}) => {
    const headerStyles = useHeaderStyles();
    const classes = useStyles();
    const theme = useTheme();
    const [avatarColor] = useState(randomColor.getColor());

    let subheader;
    switch (document?.document_type) {
        case 'cg719s':
        case 'cod':
        case 'ssl':
        case 'noaatoss':
        case 'armytoss':
        case 'uscgtoss':
        case 'uscg_aops':
            subheader = `${Math.round(subtitle * 10) / 10} Sea Days Credit`;
            break;
        default:
            subheader = subtitle;
            break;
    }

    let link;
    switch (document?.document_type) {
        case 'trainingcert':
            link = `/document/courses/${document?.document_id}`;
            break;
        case 'noaatoss':
        case 'armytoss':
        case 'uscgtoss':
            link = `/document/ssl/${document?.document_id}`;
            break;
        case 'ssl':
        case 'cod':
        case 'cg719s':
        case 'uscg_aops':
        case 'cpr':
        case 'nvic':
        case 'toar':
            link = `/document/${document?.document_type}/${document?.document_id}`;
            break;
        case 'tankerman':
            link = `/document/tankerman/${document?.document_id}`;
            break;
        default:
            link = `/document/supporting_document/${document?.document_id}`;
            break;
    }
    const image = get(document, 'user_images[0].thumb_signed_url');

    let buttonProps;
    switch (true) {
        case !!document && !onClick:
            buttonProps = {
                to: link,
                component: Link,
            };
            break;
        case typeof onClick === 'function':
            buttonProps = {
                onClick,
                disabled: !!error,
            };
            break;
        default:
            buttonProps = {
                onClick: () => {},
                disabled: !!error,
            };
    }

    if (variant === 'listItem') {
        return (
            <ButtonBase {...buttonProps}>
                <Card
                    className={clsx(
                        classes.listItemRoot,
                        selected ? classes.listItemSelected : classes.listItem
                    )}
                >
                    <Box style={{display: 'flex', flexDirection: 'column'}}>
                        <CardContent sx={{flex: '1 0 auto'}}>
                            <Typography component='div' variant='h5'>
                                {title}
                            </Typography>
                            <Typography
                                variant='body2'
                                color='text.secondary'
                                component='div'
                                className={classes.subtitle}
                            >
                                {subtitle}
                            </Typography>
                            <Typography
                                variant='overline'
                                color='text.secondary'
                                component='div'
                            >
                                {otherData}
                            </Typography>
                        </CardContent>
                    </Box>
                    <Show condition={image}>
                        <CardMedia
                            component='img'
                            style={{width: 240}}
                            src={image}
                            alt=''
                        />
                    </Show>
                </Card>
            </ButtonBase>
        );
    } else {
        return (
            <ButtonBase {...buttonProps}>
                <Card
                    className={classes.root}
                    style={{
                        background: selected
                            ? theme.palette.primary.light4
                            : null,
                    }}
                    data-context='cardMedia'
                >
                    <CardHeader
                        avatar={
                            <Avatar style={{backgroundColor: avatarColor}}>
                                {title ? title[0] : []}
                            </Avatar>
                        }
                        classes={headerStyles}
                        title={
                            title ||
                            document?.document_type
                                ?.replaceAll('_', ' ')
                                .toUpperCase()
                        }
                        subheader={
                            <Typography
                                variant='subtitle1'
                                color='textSecondary'
                            >
                                {subheader}
                            </Typography>
                        }
                    />
                    {document?.user_images ? (
                        <CardMedia
                            className={classes.cover}
                            component='div'
                            image={image}
                            title='uploaded document'
                        />
                    ) : (
                        <CardMedia
                            className={classes.noImage}
                            component='div'
                            title='Preview unavailable'
                        >
                            <BrokenImage />
                        </CardMedia>
                    )}
                    <CardContent className={classes.content}>
                        <Show condition={otherData}>
                            <Typography
                                variant='subtitle1'
                                color='textSecondary'
                            >
                                {otherData}
                            </Typography>
                        </Show>
                        <Show condition={start_date}>
                            <Typography
                                variant='subtitle1'
                                color='textSecondary'
                            >
                                Start Date: {start_date}
                            </Typography>
                        </Show>
                        <Show condition={end_date}>
                            <Typography
                                variant='subtitle1'
                                color='textSecondary'
                            >
                                End Date: {end_date}
                            </Typography>
                        </Show>
                    </CardContent>
                </Card>
            </ButtonBase>
        );
    }
};

export default DocumentCard;
export {useHeaderStyles};
